import dayjs, { Dayjs } from 'dayjs';
import { get } from 'lodash';
import { useTranslation } from 'react-i18next';

import { FormField } from 'core/components';
import { InputType } from 'core/enums';
import { useFormContext } from 'core/states';
import { objectMapToSelectOptions } from 'core/utils';
import { getNoDeliveryDateSubmissionDay, getShipSelectionLabels } from 'quotes/utils';

import { ShipSelection } from './enums';

interface IProps {
  required?: boolean;
  disabled?: boolean;
  // Submit drawer only: switching to No Delivery Date shows the local submission day that is sent.
  seedNoDeliveryDateSubmissionDay?: boolean;
}

export const ShipSelectionDateFields = ({
  required = false,
  disabled = false,
  seedNoDeliveryDateSubmissionDay = false,
}: IProps) => {
  const { t } = useTranslation();

  const { control, errors, watch, setValue, getValues, defaultValues } = useFormContext();

  const shipSelection = watch('shipSelection') as ShipSelection;
  const earliestAcceptableDate = watch('earliestAcceptableDate');

  // Each option locks/defaults the two delivery dates differently (see the per-option spec).
  // By/Within "leave" the current Requested — derived purely from the watched selection.
  const isEarliestLocked =
    shipSelection === ShipSelection.NO_DELIVERY_DATE || shipSelection === ShipSelection.DELIVERY_BY;
  const isRequestedLocked =
    shipSelection === ShipSelection.NO_DELIVERY_DATE ||
    shipSelection === ShipSelection.DELIVERY_ON ||
    shipSelection === ShipSelection.DELIVERY_AFTER;

  // Apply each option's entry state on user selection. The Select controller has already written
  // newValue to RHF, so we only layer the per-option date transitions on top of it. Branch on the
  // newValue arg (not `shipSelection`, which is the pre-change render value here).
  const onShipSelectionChange = (newValue: ShipSelection) => {
    const today = dayjs();

    switch (newValue) {
      case ShipSelection.NO_DELIVERY_DATE:
        // NDD earliest is the submission day: never editable, never saved. Restore the loaded NDD
        // value; outside the submit drawer a fresh NDD selection stays blank until submit.
        if (seedNoDeliveryDateSubmissionDay) {
          setValue('earliestAcceptableDate', getNoDeliveryDateSubmissionDay());
        } else {
          setValue(
            'earliestAcceptableDate',
            get(defaultValues, 'shipSelection') === ShipSelection.NO_DELIVERY_DATE
              ? get(defaultValues, 'earliestAcceptableDate') ?? null
              : null
          );
        }
        setValue('requiredOnSiteDate', null);
        break;
      case ShipSelection.DELIVERY_BY:
        setValue('earliestAcceptableDate', null);
        break;
      case ShipSelection.DELIVERY_WITHIN:
        if (!getValues('earliestAcceptableDate')) {
          setValue('earliestAcceptableDate', today);
        }
        break;
      case ShipSelection.DELIVERY_ON: {
        const earliest = getValues('earliestAcceptableDate') || today;
        setValue('earliestAcceptableDate', earliest);
        setValue('requiredOnSiteDate', earliest);
        break;
      }
      case ShipSelection.DELIVERY_AFTER:
        if (!getValues('earliestAcceptableDate')) {
          setValue('earliestAcceptableDate', today);
        }
        setValue('requiredOnSiteDate', null);
        break;
      default:
        break;
    }
  };

  // Delivery On mirrors Requested onto Earliest. shipSelection is unchanged in this handler, so the
  // render-scope value is current; the DateInput controller already wrote earliest to RHF.
  const onEarliestChange = (value: Dayjs | null) => {
    if (shipSelection === ShipSelection.DELIVERY_ON) {
      setValue('requiredOnSiteDate', value ?? null);
    }
  };

  return (
    <>
      <FormField
        name="shipSelection"
        type={InputType.SELECT}
        inputProps={{
          control,
          error: get(errors, 'shipSelection'),
          label: t('labelShipSelection'),
          required,
          disabled,
          options: objectMapToSelectOptions(getShipSelectionLabels()),
          onChange: (_: unknown, newValue: ShipSelection) => onShipSelectionChange(newValue),
        }}
      />

      <FormField
        name="earliestAcceptableDate"
        type={InputType.DATE_INPUT}
        inputProps={{
          control,
          error: get(errors, 'earliestAcceptableDate'),
          label: t('labelEarliestAcceptableDate'),
          minDate: dayjs(),
          disabled: disabled || isEarliestLocked,
          required: required && !isEarliestLocked,
          onChange: onEarliestChange,
        }}
      />

      <FormField
        name="requiredOnSiteDate"
        type={InputType.DATE_INPUT}
        inputProps={{
          control,
          error: get(errors, 'requiredOnSiteDate'),
          label: t('labelRequiredOnSiteDate'),
          minDate: earliestAcceptableDate ? dayjs(earliestAcceptableDate) : dayjs(),
          disabled: disabled || isRequestedLocked,
          required: required && !isRequestedLocked,
        }}
      />
    </>
  );
};
